import { useEffect, useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { it } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const ANNO_CORRENTE = new Date().getFullYear();

const isoToDate = (v) => {
  if (!v || typeof v !== 'string') return undefined;
  const d = parse(v.slice(0, 10), 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
};

const isoToText = (v) => {
  const d = isoToDate(v);
  return d ? format(d, 'dd/MM/yyyy') : '';
};

const maschera = (raw) => {
  const n = raw.replace(/\D/g, '').slice(0, 8);
  if (n.length <= 2) return n;
  if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`;
  return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`;
};

/** Campo data con calendario in italiano e digitazione rapida (gg/mm/aaaa). */
export const DatePickerField = ({
  value,
  onChange,
  className,
  testId,
  disabled,
  placeholder = 'gg/mm/aaaa',
  fromYear = 1900,
  toYear = ANNO_CORRENTE + 5,
}) => {
  const [open, setOpen] = useState(false);
  const [testo, setTesto] = useState(isoToText(value));
  const selected = isoToDate(value);

  useEffect(() => { setTesto(isoToText(value)); }, [value]);

  const scriviTesto = (raw) => {
    const t = maschera(raw);
    setTesto(t);
    if (t.length === 10) {
      const d = parse(t, 'dd/MM/yyyy', new Date());
      if (isValid(d) && d.getFullYear() >= 1900) onChange(format(d, 'yyyy-MM-dd'));
    } else if (t === '') {
      onChange('');
    }
  };

  const chiudiTesto = () => {
    if (testo.length !== 10 && testo !== '') setTesto(isoToText(value));
  };

  return (
    <div className={cn('relative', className)}>
      <Input
        value={testo}
        disabled={disabled}
        placeholder={placeholder}
        inputMode="numeric"
        data-testid={testId}
        onChange={(e) => scriviTesto(e.target.value)}
        onBlur={chiudiTesto}
        className="pr-9"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Apri calendario"
            data-testid={testId ? `${testId}-calendario` : undefined}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-slate-400 transition-colors hover:text-brand hover:bg-slate-100 disabled:opacity-40"
          >
            <CalendarIcon className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={it}
            selected={selected}
            defaultMonth={selected}
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={toYear}
            onSelect={(d) => {
              onChange(d ? format(d, 'yyyy-MM-dd') : '');
              setOpen(false);
            }}
            classNames={{
              caption_dropdowns: 'flex gap-1.5 items-center',
              dropdown: 'text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white',
              dropdown_month: 'relative',
              dropdown_year: 'relative',
              caption_label: 'sr-only',
              vhidden: 'sr-only',
            }}
            initialFocus
          />
          <div className="border-t p-2 flex justify-between gap-2">
            <Button type="button" variant="ghost" size="sm"
              data-testid={testId ? `${testId}-oggi` : undefined}
              onClick={() => { onChange(format(new Date(), 'yyyy-MM-dd')); setOpen(false); }}>
              Oggi
            </Button>
            <Button type="button" variant="ghost" size="sm"
              data-testid={testId ? `${testId}-clear` : undefined}
              onClick={() => { onChange(''); setTesto(''); setOpen(false); }}>
              Cancella
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DatePickerField;
